import React, { useEffect, useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import { Rocket, Info, Minus, Plus } from 'lucide-react';

const CHIPS = [1, 2, 5, 10];

export default function CrashGame({ game }) {
  const Icon = Icons[game.icon] || Rocket;
  const [phase, setPhase] = useState('idle'); // idle | running | cashed | crashed
  const [mult, setMult] = useState(1.0);
  const [bet, setBet] = useState(1);
  const [msg, setMsg] = useState('Set your stake and launch.');
  const [history, setHistory] = useState([2.34, 1.08, 5.71, 1.92, 3.15]);
  const [lastWin, setLastWin] = useState(null);
  const crashAt = useRef(2);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const launch = () => {
    if (phase === 'running') return;
    setLastWin(null);
    setPhase('running');
    setMult(1.0);
    setMsg('Climbing... cash out before it crashes.');
    // deterministic-feel demo crash point
    crashAt.current = Math.round((1.15 + Math.random() * 6.5) * 100) / 100;
    timer.current = setInterval(() => {
      setMult((m) => {
        const next = Math.round((m + 0.02 + m * 0.012) * 100) / 100;
        if (next >= crashAt.current) {
          clearInterval(timer.current);
          setPhase('crashed');
          setMsg(`Crashed at ${crashAt.current.toFixed(2)}x — no cash out this round. (Demo)`);
          setHistory((h) => [crashAt.current, ...h].slice(0, 8));
          return crashAt.current;
        }
        return next;
      });
    }, 55);
  };

  const cashOut = () => {
    if (phase !== 'running') return;
    clearInterval(timer.current);
    setPhase('cashed');
    const win = Math.round(bet * mult * 100) / 100;
    setLastWin(win);
    setMsg(`Cashed out at ${mult.toFixed(2)}x — demo win €${win.toFixed(2)}.`);
    setHistory((h) => [mult, ...h].slice(0, 8));
  };

  const running = phase === 'running';
  const crashed = phase === 'crashed';
  const color = crashed ? '#FF204E' : running ? '#2FBF71' : '#B026FF';

  return (
    <div className="xc-fade-up">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg grid place-items-center" style={{ background: game.grad }}><Icon className="h-4 w-4 text-white" /></span>
          <span className="font-display font-700 text-white">{game.title}</span>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/50">Provider Game Demo</span>
      </div>

      <div className="grid md:grid-cols-[1fr_240px] gap-5 items-start">
        {/* Stage */}
        <div className="relative rounded-2xl p-6 overflow-hidden border h-64 grid place-items-center" style={{ background: 'linear-gradient(160deg,#151119,#0D0B10)', borderColor: `${color}44` }}>
          <div className="absolute inset-0" style={{ background: `radial-gradient(70% 70% at 50% 90%, ${color}22, transparent 60%)` }} />
          <div className="absolute inset-0 xc-grid opacity-20" />
          <Rocket className={`absolute h-6 w-6 transition-all duration-300 ${running ? 'text-[#2FBF71]' : crashed ? 'text-[#FF204E]' : 'text-white/30'}`}
            style={{ bottom: `${Math.min(80, (mult - 1) * 22)}%`, left: `${Math.min(80, (mult - 1) * 20 + 8)}%`, transform: `rotate(-35deg) ${crashed ? 'scale(0.8)' : ''}` }} />
          <div className="relative text-center">
            <div className="font-display font-700 text-6xl" style={{ color, textShadow: `0 0 30px ${color}` }}>{mult.toFixed(2)}x</div>
            <div className={`mt-2 text-sm ${crashed ? 'text-[#FF204E]' : 'text-white/55'}`}>{msg}</div>
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-3">
          <div className="rounded-lg px-3 py-2.5 bg-white/[0.04] border border-white/10 flex items-center justify-between">
            <span className="text-[10px] text-white/40 uppercase">Balance</span><span className="text-white font-600 text-sm">€183.40</span>
          </div>
          <div className="rounded-lg p-3 bg-white/[0.04] border border-white/10">
            <div className="flex items-center justify-between mb-2"><span className="text-[10px] text-white/40 uppercase">Stake</span><span className="text-white font-600 text-sm">€{bet.toFixed(2)}</span></div>
            <div className="flex items-center gap-2">
              <button disabled={running} onClick={() => setBet((b) => Math.max(1, b - 1))} className="btn-ghost rounded-md h-8 w-8 grid place-items-center disabled:opacity-40"><Minus className="h-3.5 w-3.5" /></button>
              <div className="flex-1 flex gap-1">
                {CHIPS.map((c) => <button key={c} disabled={running} onClick={() => setBet(c)} className={`flex-1 rounded-md py-1.5 text-xs font-600 border ${bet === c ? 'border-[#B026FF]/60 text-white bg-[#B026FF]/10' : 'border-white/10 text-white/50'} disabled:opacity-40`}>€{c}</button>)}
              </div>
              <button disabled={running} onClick={() => setBet((b) => Math.min(20, b + 1))} className="btn-ghost rounded-md h-8 w-8 grid place-items-center disabled:opacity-40"><Plus className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          {running ? (
            <button onClick={cashOut} className="btn-gold rounded-xl w-full py-4 font-700 tracking-widest">CASH OUT €{(bet * mult).toFixed(2)}</button>
          ) : (
            <button onClick={launch} className="btn-neon rounded-xl w-full py-4 font-700 tracking-widest">LAUNCH</button>
          )}
          <p className="text-[11px] text-white/40 leading-relaxed flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> Demo only — no real money. The provider controls the crash point; Xincity only reacts.</p>
        </div>
      </div>

      {/* History */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wide text-white/40">Recent</span>
        {history.map((h, i) => (
          <span key={i} className="text-xs font-600 px-2 py-1 rounded-md border" style={{ color: h >= 2 ? '#2FBF71' : '#FF204E', borderColor: h >= 2 ? '#2FBF7140' : '#FF204E40', background: h >= 2 ? '#2FBF7112' : '#FF204E12' }}>{h.toFixed(2)}x</span>
        ))}
      </div>
    </div>
  );
}
